import { Controller } from '@hotwired/stimulus';
import Sortable from 'sortablejs';
import { searchPlants } from '../lib/plant_search';
import Store from './store';
import Renderer, { contentOffscreen } from './canvas/renderer';
import Interactions from './canvas/interactions';
import UndoStack from './undo';
import {
  loadPlanData, PlanLoadError, savePlan, createPlan, saveDraft, saveDraftHandoff,
  loadDraft, loadDraftCandidate, clearDraft, clearDraftHandoff,
} from './data';
import { toPlanPayload, toContent, toDraftContent, snapshot } from './serialize';
import { makePlantElement } from './plants';
import { DEFAULT_LAYERS, migrate } from './migrations';
import { dateRange, monthLabel, todayMonth } from './dates';
import { plannerSignInUrl } from '../lib/planner_auth';
import { parseSaveErrorBody } from '../lib/plan_save_errors';
import { IMAGE_ACCEPT, IMAGE_FORMATS_HINT, capturePlannerImageUpload } from '../lib/image_upload';
import { displaySideLength, planSideLength, setStraightSideLength, straightPathSides } from './path_sides';

const AUTOSAVE_MS = 2000;
const DRAFT_MS = 400;

// Root controller for the v2 planner. Owns store + Konva renderer + interactions
// and orchestrates mutations (undo, autosave, dirty-state).
export default class extends Controller {
  static values = {
    dataUrl: String, saveUrl: String, imageUrl: String, username: String, slug: String,
    new: Boolean, createUrl: String, preloadPlant: String, viewOnly: Boolean,
  };
  static targets = ['canvas', 'loading', 'save', 'undo', 'redo', 'zoom', 'zoomMenu', 'jump', 'tabs', 'timeline', 'scalebar', 'snap', 'searchInput', 'searchResults', 'tipbar'];

  connect() {
    this.store = new Store();
    this.undoStack = new UndoStack();
    this.renderer = new Renderer(this.canvasTarget, this.store);
    this.renderer.controller = this; // lets the renderer commit image-edit gestures
    this.interactions = new Interactions(this.renderer, this.store, this);
    this.interactions.bind();
    this.renderer.setDraggable(!this.viewOnlyValue); // view-only: nothing draggable
    if (this.viewOnlyValue) this.store.set({ activeTool: 'navigate' });
    this._hits = [];

    this.unsub = this.store.subscribe((state) => { this.syncChrome(state); this.renderPanels(state); });
    if (!this.viewOnlyValue) {
      this._onKey = (e) => this.onKey(e);
      window.addEventListener('keydown', this._onKey);
      // New plans are not autosaved, so warn before the tab closes with unsaved work.
      // Controlled navigation persists a draft first and bypasses this warning.
      this._onBeforeUnload = (e) => {
        if (this.store.state.dirty && !this._allowPageExit) {
          clearTimeout(this._draftT);
          saveDraft(this.usernameValue, this.slugValue || 'new', toDraftContent(this.store.state));
          e.preventDefault();
          e.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', this._onBeforeUnload);
    }
    this.element.addEventListener('change', (e) => this.onPanelChange(e));
    this.element.addEventListener('click', (e) => this.onPanelClick(e));
    this._onHover = (e) => this.onSpeciesHover(e);
    this.element.addEventListener('mouseover', this._onHover);
    this.element.addEventListener('mouseout', (e) => { if (e.target.closest?.('[data-species-info]')) this.hideSpeciesFlyout(); });

    window.planner2 = { store: this.store, renderer: this.renderer, controller: this, serialize: () => toContent(this.store.state) };
    if (this.newValue) this.initNewPlan(); else this.load();
  }

  // Start an empty plan (no server fetch). First save POSTs to #create.
  initNewPlan() {
    this.store.loadPlan({
      version: 4, name: null,
      view: { scale: 1, posX: 0, posY: 0 },
      settings: { name: null, access: 'public', showGrid: true, snapToGrid: true, showLabels: true, units: 'metric', autoSave: true },
      layers: DEFAULT_LAYERS, elements: [], species: {},
    });
    this.renderer.fitToContent();
    this.applySettings();
    if (this.hasLoadingTarget) this.loadingTarget.remove();
    if (this.preloadPlantValue) {
      try { this.startPlacement(JSON.parse(this.preloadPlantValue)); } catch (_) { /* ignore */ }
    }
    this.maybeRestoreDraft();
    this.maybeWelcome();
  }

  disconnect() {
    this.closeContextMenu();
    window.removeEventListener('keydown', this._onKey);
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    clearTimeout(this._autoT); clearTimeout(this._draftT);
    this._layerSort?.destroy();
    this.hideSpeciesFlyout();
    this.clearHint();
    this.unsub?.();
    this.renderer?.destroy();
    delete window.planner2;
  }

  async load() {
    try {
      const plan = await loadPlanData(this.dataUrlValue);
      this.store.loadPlan(plan);
      if (!(plan.view && plan.view.scale)) this.renderer.fitToContent();
      this.applySettings();
      if (this.hasLoadingTarget) this.loadingTarget.remove();
      this.maybeRestoreDraft();
      this.checkStrays();
    } catch (e) {
      this.showLoadError();
      this.reportLoadError(e);
    }
  }

  // Report a load failure under a stable exception name so every failure groups
  // into one issue, with the status as an event property — instead of a raw,
  // engine-worded body-read exception that opens a fresh issue per browser. A
  // transport failure (a fetch that never landed, or a body the browser could
  // not finish reading) or an expired session (401) is a transient, non-code
  // problem: the user still sees the retry panel and keeps their plan on the
  // server, so neither is filed as a bug.
  reportLoadError(e) {
    if (e.isNetworkError || e.status === 401) return;
    if (e instanceof PlanLoadError) {
      window.posthog?.captureException?.(new Error('Plan data load failed'), {
        load_status: e.status,
        load_url: this.dataUrlValue,
      });
      return;
    }
    // A genuine data or render error further down load() is a real bug — report it as-is.
    window.posthog?.captureException?.(e);
  }

  showLoadError() {
    if (!this.hasLoadingTarget) return;
    this.loadingTarget.innerHTML = `
      <p>We could not load this plan. Check your connection, then try again.</p>
      <button class="pl2-btn" type="button" data-action="planner#retryLoad">Retry</button>`;
  }

  retryLoad() {
    if (this.hasLoadingTarget) this.loadingTarget.textContent = 'Loading plan…';
    this.load();
  }

  // Offer the same-tab authentication handoff before an older account draft.
  maybeRestoreDraft() {
    const slug = this.slugValue || 'new';
    const candidate = loadDraftCandidate(this.usernameValue, slug);
    if (candidate) this.offerDraft(candidate.draft, candidate.handoff, candidate.handoffToken);
  }

  maybeRestoreAccountDraft() {
    const draft = loadDraft(this.usernameValue, this.slugValue || 'new');
    if (draft) this.offerDraft(draft, false, null);
  }

  offerDraft(draft, handoff, handoffToken) {
    const slug = this.slugValue || 'new';
    try {
      if (JSON.stringify(draft) === JSON.stringify(toDraftContent(this.store.state))) {
        if (handoff) clearDraftHandoff(handoffToken);
        else clearDraft(this.usernameValue, slug);
        if (handoff) this.maybeRestoreAccountDraft();
        return;
      }
    } catch (_) { /* compare best-effort */ }
    this.showDraftBanner(draft, handoff, handoffToken);
  }

  showDraftBanner(draft, handoff = false, handoffToken = null) {
    this.element.querySelector('.pl2-draft-banner')?.remove();
    const slug = this.slugValue || 'new';
    const bar = document.createElement('div');
    bar.className = 'pl2-draft-banner';
    bar.innerHTML = `
      <span>Unsaved changes from a previous session.</span>
      <button class="pl2-btn" type="button" data-draft="restore">Restore</button>
      <button class="btn-clear-light" type="button" data-draft="discard">Discard</button>`;
    bar.querySelector('[data-draft="restore"]').addEventListener('click', () => {
      if (handoff && !saveDraft(this.usernameValue, slug, toDraftContent(draft))) {
        this.showDraftPersistenceError();
        return;
      }
      if (handoff) clearDraftHandoff(handoffToken);
      this.restoreDraft(draft);
      bar.remove();
    });
    bar.querySelector('[data-draft="discard"]').addEventListener('click', () => {
      if (handoff) clearDraftHandoff(handoffToken);
      else clearDraft(this.usernameValue, slug);
      bar.remove();
      if (handoff) this.maybeRestoreAccountDraft();
    });
    this.element.appendChild(bar);
  }

  restoreDraft(draft) {
    this.store.loadPlan(migrate(draft));
    this.applySettings();
    this.markDirty();
  }

  // Warn when the plan has stray objects far from everything else (they distort
  // the view). Offer to reveal them or delete them. View-only plans just inform.
  checkStrays() {
    if (this.viewOnlyValue) return;
    const ids = this.renderer.strayElementIds(this.store.state);
    if (ids.length) this.showStrayBanner(ids);
  }

  showStrayBanner(ids) {
    this.element.querySelector('.pl2-stray-banner')?.remove();
    const n = ids.length;
    const bar = document.createElement('div');
    bar.className = 'pl2-stray-banner';
    bar.innerHTML = `
      <span>⚠ ${n} object${n > 1 ? 's' : ''} sit far from the rest of your plan and can distort the view.</span>
      <button class="pl2-btn" type="button" data-stray="show">Show</button>
      <button class="pl2-danger-btn pl2-btn" type="button" data-stray="delete">Delete ${n > 1 ? 'them' : 'it'}</button>
      <button class="pl2-iconbtn" type="button" data-stray="dismiss" title="Dismiss">×</button>`;
    bar.querySelector('[data-stray="show"]').addEventListener('click', () => {
      this.store.set({ selection: this.unlockedElementIds(ids) });
      this.renderer.fitAll();
    });
    bar.querySelector('[data-stray="delete"]').addEventListener('click', () => {
      const set = new Set(this.unlockedElementIds(ids));
      if (set.size) {
        this.commit(this.takeSnapshot(), () => {
          this.store.state.elements = this.store.state.elements.filter((e) => !set.has(e.id));
          this.store.set({ selection: [] });
        });
        this.renderer.fitToContent();
      }
      bar.remove();
    });
    bar.querySelector('[data-stray="dismiss"]').addEventListener('click', () => bar.remove());
    this.element.appendChild(bar);
  }

  unlockedElementIds(ids) {
    const requested = new Set(ids);
    return this.store.state.elements
      .filter((element) => requested.has(element.id) && !element.data?.locked)
      .map((element) => element.id);
  }

  // Drag-to-reorder the layers list (rebuilt on every render, so re-init each time).
  initLayerSort(list) {
    if (!list || this.viewOnlyValue) return;
    this._layerSort?.destroy();
    this._layerSort = Sortable.create(list, {
      handle: '[data-drag-handle]',
      draggable: '.pl2-row',
      animation: 120,
      onEnd: () => this.reorderLayersFromDom(list),
    });
  }

  reorderLayersFromDom(list) {
    const ids = Array.from(list.querySelectorAll('[data-layer-id]')).map((r) => r.dataset.layerId);
    const cur = this.store.state.layers;
    const byId = new Map(cur.map((l) => [l.id, l]));
    const next = ids.map((id) => byId.get(id)).filter(Boolean);
    if (next.length !== cur.length || next.every((l, i) => l === cur[i])) return; // unchanged
    this.commit(this.takeSnapshot(), () => { this.store.state.layers = next; });
  }

  // --- mutation plumbing ----------------------------------------------------
  takeSnapshot() { return snapshot(this.store.state); }

  // Commit a mutation: optionally run `fn`, record the prior snapshot for undo,
  // re-render, mark dirty (debounced autosave + draft).
  commit(prevSnapshot, fn) {
    if (fn) fn();
    this.undoStack.push(prevSnapshot);
    this.store.emit({ source: 'commit' });
    this.markDirty();
    this.updateUndoButtons();
  }

  markDirty() {
    this.store.state.dirty = true;
    this.saveTarget?.classList.add('is-dirty');
    clearTimeout(this._draftT);
    this._draftT = setTimeout(() => {
      if (!saveDraft(this.usernameValue, this.slugValue || 'new', toDraftContent(this.store.state))) {
        this.showDraftPersistenceError();
      }
    }, DRAFT_MS);
    // New plans aren't autosaved (no record yet) — they're created on explicit
    // Save. Autosave also stays off after the server rejected a save, until an
    // explicit save succeeds, so one stale page can't retry in a loop.
    if (!this.newValue && !this._saveRejected) {
      clearTimeout(this._autoT);
      this._autoT = setTimeout(() => this.save(), AUTOSAVE_MS);
    }
  }

  async save() {
    clearTimeout(this._autoT);
    if (this.newValue) return this.saveNewPlan();
    try {
      await savePlan(this.saveUrlValue, toPlanPayload(this.store.state));
      this.store.state.dirty = false;
      this.saveTarget?.classList.remove('is-dirty');
      this._saveRejected = false;
      clearDraft(this.usernameValue, this.slugValue);
      this.captureOverview();
    } catch (e) {
      // A save the server rejected (e.g. an expired page session) keeps failing
      // until the user reloads, so halt autosave to stop a burst of identical
      // errors. A network rejection is transient, so leave autosave running to
      // retry when the connection returns.
      if (!e.isNetworkError) this._saveRejected = true;
      this.showError(e.message, e.sessionExpired);
      this.reportSaveError(e, this.saveUrlValue);
    }
  }

  async saveNewPlan() {
    if (!this.usernameValue) { this.promptGuestToSignIn(); return; }
    let name = this.store.state.settings?.name;
    if (!name || !name.trim()) {
      name = await this.promptName();
      if (name == null) return; // cancelled
      this.store.state.settings = { ...this.store.state.settings, name: name.trim() };
    }
    try {
      const res = await createPlan(this.createUrlValue, toPlanPayload(this.store.state));
      this.store.state.dirty = false;
      clearDraft(this.usernameValue, this.slugValue || 'new');
      // #create returns the classic /edit uri; open the v2 editor for the new plan.
      window.location.href = String(res.uri || '').replace(/\/edit$/, '/edit-v2');
    } catch (e) {
      this.showError(e.message, e.sessionExpired);
      this.reportSaveError(e, this.createUrlValue);
    }
  }

  persistDraftForPageExit() {
    this._allowPageExit = false;
    clearTimeout(this._draftT);
    const saved = saveDraft(this.usernameValue, this.slugValue || 'new', toDraftContent(this.store.state));
    if (!saved) {
      this.showDraftPersistenceError();
      return false;
    }
    this._allowPageExit = true;
    return true;
  }

  navigateWithDraft(url) {
    if (!this.persistDraftForPageExit()) return false;
    window.location.href = url;
    return true;
  }

  refreshWithDraft() {
    if (!this.persistDraftForPageExit()) return false;
    window.location.reload();
    return true;
  }

  showDraftPersistenceError() {
    this.showError('We could not store your latest changes in this browser. Keep this page open, free some browser storage, and try again.');
  }

  promptGuestToSignIn() {
    // Save immediately instead of waiting for the draft timer.
    clearTimeout(this._draftT);
    if (!saveDraft(this.usernameValue, this.slugValue || 'new', toDraftContent(this.store.state))) {
      this.showDraftPersistenceError();
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Save your garden plan</h3></header>
        <section class="modal-content"><p>Create a free account or sign in to save your plan. Your work is kept while you do — it will be here when you get back.</p></section>
        <div class="modal-footer"><button class="btn-clear-light" data-act="cancel" type="button">Keep editing</button><button class="btn-primary" data-act="ok" type="button">Sign in to save</button></div>
      </div>`;
    const close = () => overlay.remove();
    overlay.querySelector('[data-act="ok"]').addEventListener('click', () => {
      const content = toDraftContent(this.store.state);
      if (!saveDraft(this.usernameValue, this.slugValue || 'new', content) || !saveDraftHandoff(content)) {
        this.showDraftPersistenceError();
        return;
      }
      this._allowPageExit = true;
      window.location.href = plannerSignInUrl(window.location.href);
    });
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', close);
    overlay.addEventListener('mousedown', (event) => { if (event.target === overlay) close(); });
    this.element.appendChild(overlay);
  }

  // Report a save failure under a stable exception name so every failure groups
  // into one issue, with the detail as event properties. A network-level
  // rejection or an expired page session is a transient, non-code problem (the
  // user still sees an error and keeps their local draft), so neither is
  // reported as a code bug.
  reportSaveError(e, url) {
    if (e.isNetworkError || e.sessionExpired) return;
    window.posthog?.captureException?.(new Error('Plan save failed'), {
      save_status: e.status,
      save_message: e.message,
      save_url: url,
    });
  }

  // Simple name prompt modal; resolves to the entered name or null if cancelled.
  promptName() {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.display = 'flex';
      overlay.innerHTML = `
        <div class="modal-container">
          <header class="modal-header"><h3>Name your plan</h3></header>
          <section class="modal-content"><input class="input-text pl2-input" type="text" placeholder="Garden plan" style="width:100%"></section>
          <div class="modal-footer"><button class="btn-clear-light" data-act="cancel" type="button">Cancel</button><button class="btn-primary" data-act="ok" type="button">Save</button></div>
        </div>`;
      const input = overlay.querySelector('input');
      const done = (val) => { overlay.remove(); resolve(val); };
      overlay.querySelector('[data-act="ok"]').addEventListener('click', () => done(input.value.trim() || 'Untitled plan'));
      overlay.querySelector('[data-act="cancel"]').addEventListener('click', () => done(null));
      overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) done(null); });
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') done(input.value.trim() || 'Untitled plan'); if (e.key === 'Escape') done(null); });
      this.element.appendChild(overlay);
      input.focus();
    });
  }

  undo() {
    if (!this.undoStack.canUndo()) return;
    this.applySnapshot(this.undoStack.undo(this.takeSnapshot()));
  }

  redo() {
    if (!this.undoStack.canRedo()) return;
    this.applySnapshot(this.undoStack.redo(this.takeSnapshot()));
  }

  applySnapshot(s) {
    if (!s) return;
    Object.assign(this.store.state, { elements: s.elements, layers: s.layers, settings: s.settings, selection: [] });
    this.store.emit({ source: 'undo' });
    this.markDirty();
    this.updateUndoButtons();
  }

  updateUndoButtons() {
    if (this.hasUndoTarget) this.undoTarget.disabled = !this.undoStack.canUndo();
    if (this.hasRedoTarget) this.redoTarget.disabled = !this.undoStack.canRedo();
  }

  // --- toolbar / tabs -------------------------------------------------------
  setTool(event) {
    this.applyTool(event.currentTarget.dataset.tool, 'user');
  }

  // `source` is 'user' for a toolbar click or keyboard shortcut, and unset for
  // the internal auto-switches back to 'select', so only real picks are tracked.
  applyTool(tool, source) {
    this.interactions.cancelPoly();
    this.renderer.clearPreview();
    this.clearHint();
    this.store.set({ activeTool: tool, pendingSpecies: tool === 'placement' ? this.store.state.pendingSpecies : null });
    if (tool === 'navigate') this.renderer.setHover(null);
    this.renderer.setDraggable(tool === 'select');
    this.syncToolButtons();
    if (source === 'user') window.posthog?.capture('planner_tool_selected', { tool, planner_version: 'v2' });
  }

  syncToolButtons() {
    const tool = this.store.state.activeTool;
    this.element.querySelectorAll('.pl2-tool[data-tool]').forEach((btn) => {
      const active = btn.dataset.tool === tool;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  setTab(event) {
    const tab = event.currentTarget.dataset.tab;
    this.store.set({ activeTab: tab });
    this.element.querySelectorAll('.pl2-tab[data-tab]').forEach((b) => b.classList.toggle('is-active', b.dataset.tab === tab));
    this.element.querySelectorAll('[data-tab-panel]').forEach((p) => { p.hidden = p.dataset.tabPanel !== tab; });
    if (tab === 'lists') this.loadLists();
  }

  // --- zoom ----------------------------------------------------------------
  resetZoom() { this.renderer.fitToContent(); }

  zoomCenter() { return { x: this.renderer.stage.width() / 2, y: this.renderer.stage.height() / 2 }; }

  zoomToScaleAt(scale, pt) {
    const v = this.store.state.view; const old = v.scale || 1;
    const clamped = Math.min(50, Math.max(0.02, scale));
    const mx = (pt.x - (v.posX || 0)) / old;
    const my = (pt.y - (v.posY || 0)) / old;
    this.store.set({ view: { scale: clamped, posX: pt.x - mx * clamped, posY: pt.y - my * clamped } }, { source: 'zoom' });
  }

  zoomIn() { this.zoomToScaleAt((this.store.state.view.scale || 1) * 1.2, this.zoomCenter()); }
  zoomOut() { this.zoomToScaleAt((this.store.state.view.scale || 1) / 1.2, this.zoomCenter()); }
  zoom25() { this.zoomToScaleAt(0.25, this.zoomCenter()); this.closeZoomMenu(); }
  zoom50() { this.zoomToScaleAt(0.5, this.zoomCenter()); this.closeZoomMenu(); }
  zoom100() { this.zoomToScaleAt(1, this.zoomCenter()); this.closeZoomMenu(); }
  zoom200() { this.zoomToScaleAt(2, this.zoomCenter()); this.closeZoomMenu(); }
  zoomFit() { this.renderer.fitToContent(); this.closeZoomMenu(); }
  toggleZoomMenu() { if (this.hasZoomMenuTarget) this.zoomMenuTarget.hidden = !this.zoomMenuTarget.hidden; }
  closeZoomMenu() { if (this.hasZoomMenuTarget) this.zoomMenuTarget.hidden = true; }

  // Mobile: the left panel slides in/out as an overlay (CSS-driven).
  togglePanel() { this.element.classList.toggle('pl2-panel-open'); }
  closePanel() { this.element.classList.remove('pl2-panel-open'); }

  // --- image elements ------------------------------------------------------
  openImagePanel() {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    const body = this.newValue || !this.imageUrlValue
      ? '<p class="pl2-muted">Save the plan before you add an image.</p>'
      : `<p class="pl2-muted">Add one or more satellite, sketch, or reference images.</p>
         <label class="pl2-btn" style="display:inline-flex">Choose images…<input type="file" accept="${IMAGE_ACCEPT}" data-img="file" multiple hidden></label>
         <p class="pl2-muted">${IMAGE_FORMATS_HINT}</p>`;
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Add images</h3></header>
        <section class="modal-content">${body}</section>
        <div class="modal-footer"><button class="btn-primary" type="button" data-act="close">Done</button></div>
      </div>`;
    const close = () => overlay.remove();
    overlay.querySelector('[data-act="close"]').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('[data-img="file"]')?.addEventListener('change', async (e) => {
      for (const file of Array.from(e.target.files || [])) await this.uploadImage(file);
      e.target.value = '';
    });
    this.element.appendChild(overlay);
  }

  // Scale calibration: click two points a known real-world distance apart. It
  // scales all element geometry so plan units (cm) read true.
  startCalibration() {
    this.interactions.cancelPoly();
    this.renderer.clearPreview();
    this.interactions._calib = [];
    this.store.set({ activeTool: 'calibrate' });
    this.renderer.setDraggable(false);
    this.syncToolButtons();
    this.showHint('Click two points a known distance apart (Esc to cancel).');
  }

  async finishCalibration(a, b) {
    const measured = Math.hypot(b.x - a.x, b.y - a.y);
    this.applyTool('select');
    this.clearHint();
    if (measured < 1) return;
    const imperial = this.store.state.settings.units === 'imperial';
    const val = await this.promptValue(`Real distance between the two points (${imperial ? 'feet' : 'metres'})`);
    if (val == null) return;
    const real = parseFloat(val);
    if (!real || real <= 0) return;
    const factor = (imperial ? real * 30.48 : real * 100) / measured;
    if (!isFinite(factor) || factor <= 0 || Math.abs(factor - 1) < 1e-6) return;
    this.commit(this.takeSnapshot(), () => this.scalePlan(factor, a));
    this.renderer.fitToContent();
  }

  // Scale every element coordinate about `origin`.
  scalePlan(factor, origin) {
    const sx = (x) => origin.x + (x - origin.x) * factor;
    const sy = (y) => origin.y + (y - origin.y) * factor;
    for (const el of this.store.state.elements) {
      const d = el.data;
      if (el.type === 'ellipse' || el.type === 'rectangle' || el.type === 'image') {
        d.x = sx(d.x); d.y = sy(d.y);
        d.width = (d.width || 0) * factor;
        if (d.height != null) d.height *= factor;
      } else if (Array.isArray(d.coordinates)) {
        d.coordinates = d.coordinates.map((c) => (Array.isArray(c) ? scaleCoord(c, origin, factor) : c));
      }
    }
  }

  // Small numeric-prompt modal; resolves to the entered string or null.
  promptValue(label) {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.display = 'flex';
      overlay.innerHTML = `
        <div class="modal-container">
          <header class="modal-header"><h3>${escapeHtml(label)}</h3></header>
          <section class="modal-content"><input class="input-text pl2-input" type="number" min="0" step="any" style="width:100%"></section>
          <div class="modal-footer"><button class="btn-clear-light" data-act="cancel" type="button">Cancel</button><button class="btn-primary" data-act="ok" type="button">OK</button></div>
        </div>`;
      const input = overlay.querySelector('input');
      const done = (v) => { overlay.remove(); resolve(v); };
      overlay.querySelector('[data-act="ok"]').addEventListener('click', () => done(input.value.trim() || null));
      overlay.querySelector('[data-act="cancel"]').addEventListener('click', () => done(null));
      overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) done(null); });
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') done(input.value.trim() || null); if (e.key === 'Escape') done(null); });
      this.element.appendChild(overlay);
      input.focus();
    });
  }

  showHint(msg) {
    this.clearHint();
    const el = document.createElement('div');
    el.className = 'pl2-hint';
    el.textContent = msg;
    this.element.appendChild(el);
    this._hint = el;
  }

  clearHint() { this._hint?.remove(); this._hint = null; }

  async uploadImage(file) {
    const form = new FormData();
    form.append('image[file]', file);
    try {
      const res = await fetch(`${this.imageUrlValue}/planner_image`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': this.csrf(), Accept: 'application/json' },
        body: form,
      });
      if (!res.ok) {
        // Show the server's validation message (wrong format or too large)
        // rather than a bare HTTP status code.
        const body = await res.json().catch(() => null);
        const { message } = parseSaveErrorBody(body, res.status);
        this.captureImageUpload('rejected', file, res.status);
        this.showError(message, false, 'We could not add that image');
        return;
      }
      const data = await res.json();
      this.captureImageUpload('success', file, res.status);
      const el = {
        id: `image-${data.id}`,
        type: 'image',
        speciesIds: [],
        data: {
          imageId: data.id,
          name: data.filename || file.name,
          url: data.url,
          x: 0,
          y: 0,
          width: null,
          height: null,
          rotation: 0,
          opacity: 200,
          visible: true,
          locked: false,
          layer: null,
          addedDate: null,
          removedDate: null,
          labels: [],
          labelX: 0,
          labelY: 0,
        },
      };
      this.applyTool('select');
      this.commit(this.takeSnapshot(), () => {
        this.store.state.elements = [...this.store.state.elements, el];
        this.store.set({ selection: [el.id] });
      });
    } catch (e) {
      this.captureImageUpload('error', file);
      this.showError(e.message, false, 'We could not add that image');
    }
  }

  captureImageUpload(result, file, status = null) {
    capturePlannerImageUpload({
      plannerVersion: 'v2',
      result,
      fileType: file?.type,
      fileSizeBytes: file?.size,
      status,
    });
  }

  csrf() { return document.querySelector('meta[name="csrf-token"]')?.content || ''; }

  // Best-effort overview thumbnail: snapshot the canvas and POST it as the plan's
  // `overview` image (shown on the plans index). Throttled to once a minute and
  // skipped for new/unsaved plans; failures are non-fatal.
  async captureOverview() {
    if (this.newValue || !this.imageUrlValue) return;
    const now = Date.now();
    if (this._lastOverview && now - this._lastOverview < 60000) return;
    this._lastOverview = now;
    try {
      const blob = await this.renderer.snapshotBlob();
      if (!blob) return;
      const form = new FormData();
      form.append('image[file]', blob, 'overview.png');
      await fetch(`${this.imageUrlValue}/overview`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': this.csrf(), Accept: 'application/json' },
        body: form,
      });
    } catch (_) { /* thumbnail is non-critical */ }
  }

  // --- timeline ------------------------------------------------------------
  toggleTimeline() {
    const on = !this.store.state.timelineOn;
    const range = dateRange(this.store.state.elements);
    let month = this.store.state.timelineMonth;
    if (on && month == null) month = range ? range.max : todayMonth();
    this.store.set({ timelineOn: on, timelineMonth: month });
    if (this.hasTimelineTarget) this.timelineTarget.hidden = !on;
    this.renderTimeline();
  }

  renderTimeline() {
    if (!this.hasTimelineTarget || !this.store.state.timelineOn) return;
    const t = todayMonth() ?? 24300;
    const range = dateRange(this.store.state.elements) || { min: t - 12, max: t + 12 };
    const month = this.store.state.timelineMonth ?? range.max;
    this.timelineTarget.innerHTML = `
      <span class="pl2-timeline-icon">${'●'}</span>
      <input class="pl2-timeline-range" type="range" min="${range.min}" max="${range.max}" step="1" value="${month}" data-action="input->planner#onTimelineInput">
      <span class="pl2-timeline-label">${monthLabel(month)}</span>
      <button class="pl2-btn pl2-timeline-today" type="button" data-action="planner#timelineToday">Today</button>
      <button class="pl2-tool pl2-zoom-step" type="button" data-action="planner#toggleTimeline" title="Close timeline">${'×'}</button>`;
  }

  onTimelineInput(e) {
    const month = Number(e.target.value);
    this.store.set({ timelineMonth: month }, { source: 'timeline' });
    const lbl = this.timelineTarget.querySelector('.pl2-timeline-label');
    if (lbl) lbl.textContent = monthLabel(month);
  }

  timelineToday() {
    const t = todayMonth();
    if (t == null) return;
    this.store.set({ timelineMonth: t });
    this.renderTimeline();
  }

  // --- settings ------------------------------------------------------------
  applySettings() {
    this.renderer.renderGrid(this.store.state, this.store.state.view || { scale: 1, posX: 0, posY: 0 });
    this.renderSnapOptions();
  }

  // --- snap to grid (Tinkercad-style select near the scale bar) -------------
  renderSnapOptions() {
    if (!this.hasSnapTarget) return;
    const metric = this.store.state.settings?.units !== 'imperial';
    const opts = metric
      ? [[0, 'Off'], [10, '10 cm'], [50, '50 cm'], [100, '1 m']]
      : [[0, 'Off'], [2.54, '1 in'], [15.24, '6 in'], [30.48, '1 ft']]; // square-foot gardening = 1 ft
    const cur = this.store.state.snapStep || 0;
    this.snapTarget.innerHTML = opts.map(([v, l]) => `<option value="${v}" ${Math.abs(v - cur) < 0.01 ? 'selected' : ''}>${l}</option>`).join('');
  }

  onSnapChange(e) { this.store.state.snapStep = Number(e.target.value); }

  openSettings() {
    const s = this.store.state.settings || {};
    const checked = (k) => (s[k] === false ? '' : 'checked');
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Plan settings</h3></header>
        <section class="modal-content">
          <div class="pl2-field"><label>Name</label><input class="pl2-input" type="text" data-s="name" value="${esc(s.name || '')}"></div>
          <div class="pl2-field"><label>Access</label><select class="pl2-input" data-s="access">
            ${['public', 'private', 'link'].map((a) => `<option value="${a}" ${s.access === a ? 'selected' : ''}>${a}</option>`).join('')}
          </select></div>
          <div class="pl2-field"><label>Units</label><select class="pl2-input" data-s="units">
            <option value="metric" ${s.units !== 'imperial' ? 'selected' : ''}>metric</option>
            <option value="imperial" ${s.units === 'imperial' ? 'selected' : ''}>imperial</option>
          </select></div>
          <label class="pl2-check"><input type="checkbox" data-s="showGrid" ${checked('showGrid')}> Show grid</label>
          <label class="pl2-check"><input type="checkbox" data-s="showLabels" ${checked('showLabels')}> Show labels</label>
          <label class="pl2-check"><input type="checkbox" data-s="autoSave" ${checked('autoSave')}> Auto-save</label>
          ${!this.newValue && this.saveUrlValue ? '<button class="pl2-btn pl2-danger-btn" type="button" data-act="delete" style="margin-top:12px">Delete plan</button>' : ''}
        </section>
        <div class="modal-footer"><button class="btn-primary" data-act="close" type="button">Done</button></div>
      </div>`;
    overlay.querySelectorAll('[data-s]').forEach((inp) => {
      inp.addEventListener('change', () => {
        const val = inp.type === 'checkbox' ? inp.checked : inp.value;
        this.commit(this.takeSnapshot(), () => { this.store.state.settings = { ...this.store.state.settings, [inp.dataset.s]: val }; });
        this.applySettings();
      });
    });
    const close = () => overlay.remove();
    overlay.querySelector('[data-act="close"]').addEventListener('click', close);
    overlay.querySelector('[data-act="delete"]')?.addEventListener('click', () => { close(); this.deletePlan(); });
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
  }

  async deletePlan() {
    if (!window.confirm('Delete this plan? This cannot be undone.')) return;
    try {
      const res = await fetch(this.saveUrlValue, { method: 'DELETE', headers: { 'X-CSRF-Token': this.csrf(), Accept: 'application/json' } });
      if (res.ok) window.location.href = `/${this.usernameValue}/plans`;
      else this.showError('Could not delete the plan.');
    } catch (e) { this.showError('Could not delete the plan.'); }
  }

  syncChrome(state) {
    if (this.hasZoomTarget) this.zoomTarget.textContent = `${Math.round((state.view?.scale || 1) * 100)}%`;
    this.updateJumpButton(state);
    this.renderScaleBar(state);
    this.updateTipBar(state);
  }

  // "Jump to content": visible only when the plan has elements but the current
  // viewport shows none of them — clicking recentres the view on the content.
  updateJumpButton(state) {
    if (!this.hasJumpTarget) return;
    const { width, height } = this.renderer.stageSize();
    this.jumpTarget.hidden = !state.loaded || !contentOffscreen(state, width, height);
  }

  jumpToContent() { this.renderer.centerOnContent(); }

  // Slim contextual hint bar at the bottom of the canvas, per active tool.
  updateTipBar(state) {
    if (!this.hasTipbarTarget) return;
    const tips = {
      select: 'Drag to move · double-click a path to add a point · right-click a line/shape → Smooth for curves',
      navigate: 'Drag to pan · scroll to zoom',
      rectangle: 'Drag to draw a raised bed, patio or building',
      ellipse: 'Drag to draw a tree canopy, pond or round bed',
      polygon: 'Click to add points · double-click to finish — beds, lawns or borders',
      line: 'Draw a fence, hedge, wall, border or path · click to add points, double-click to finish · set its thickness under Thickness',
      erase: 'Click an element to delete it',
      placement: 'Click to place · Esc to stop',
      calibrate: 'Click two points a known distance apart',
    };
    const tip = tips[state.activeTool];
    this.tipbarTarget.hidden = !tip;
    if (tip) this.tipbarTarget.textContent = tip;
  }

  // --- help / fullscreen ---------------------------------------------------
  openHelp() {
    // What each garden object maps to — the tools only name geometric shapes,
    // so spell out the real-world thing a new gardener is looking for.
    const draw = [
      ['Fence, hedge, wall or border', 'Line tool (L) — click each corner, double-click to finish. Set its thickness under Thickness in the properties panel.'],
      ['Path', 'Line tool (L) — draw the route, then raise the Thickness to the path width.'],
      ['Raised bed, patio or building', 'Rectangle tool (R) — drag a box.'],
      ['Lawn or free-form bed', 'Polygon tool (P) — click each corner, double-click to close.'],
      ['Tree canopy, pond or round bed', 'Ellipse tool (O) — drag a circle.'],
    ];
    const rows = [
      ['V', 'Select'], ['H', 'Pan'], ['R', 'Rectangle'], ['O', 'Ellipse'], ['P', 'Polygon'], ['L', 'Line'], ['E', 'Erase'],
      ['⌘Z / ⌘⇧Z', 'Undo / Redo'], ['⌘C / ⌘V / ⌘D', 'Copy / Paste / Duplicate'],
      ['[ / ]', 'Send backward / forward (⇧ = back/front)'], ['Delete', 'Delete selection'],
      ['Arrows', 'Nudge (⇧ = ×10)'], ['⌘S', 'Save'], ['Esc', 'Cancel / deselect'],
      ['Double-click path', 'Add a vertex · on a vertex: remove it'],
    ];
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>How do I draw…</h3></header>
        <section class="modal-content">
          <table class="pl2-help">${draw.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}</table>
          <h4 class="pl2-section-title" style="margin-top:16px">Keyboard shortcuts</h4>
          <table class="pl2-help">${rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}</table>
        </section>
        <div class="modal-footer"><button class="btn-primary" type="button">Done</button></div>
      </div>`;
    const close = () => overlay.remove();
    overlay.querySelector('button').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
  }

  toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else this.element.requestFullscreen?.();
  }

  selectAllOfType(type) {
    this.store.set({ selection: this.store.state.elements.filter((e) => e.type === type && !e.data?.locked).map((e) => e.id) });
  }

  // One-time welcome for a brand-new plan.
  maybeWelcome() {
    try { if (localStorage.getItem('pl2.welcomed')) return; localStorage.setItem('pl2.welcomed', '1'); } catch (_) { return; }
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Welcome to the planner</h3></header>
        <section class="modal-content">
          <p>Search for plants on the left and click the canvas to place them, or draw your garden with the shape tools up top.</p>
          <p class="pl2-muted">Beds and lawns use the Rectangle or Polygon tool. Fences, hedges, walls, borders and paths all use the <strong>Line</strong> tool — draw the run, then set its thickness under Thickness.</p>
          <p class="pl2-muted">Press <strong>?</strong> any time for help and keyboard shortcuts.</p>
        </section>
        <div class="modal-footer"><button class="btn-primary" type="button">Get started</button></div>
      </div>`;
    const close = () => overlay.remove();
    overlay.querySelector('button').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
  }

  // Species-info flyout when hovering a species row in the properties panel.
  onSpeciesHover(e) {
    const row = e.target.closest?.('[data-species-info]');
    if (!row) { this.hideSpeciesFlyout(); return; }
    const sp = this.store.state.species[row.dataset.speciesInfo];
    if (!sp) return;
    this.hideSpeciesFlyout();
    const fly = document.createElement('div');
    fly.className = 'pl2-species-flyout';
    fly.innerHTML = `
      ${sp.thumb ? `<img src="${esc(sp.thumb)}" alt="">` : ''}
      <div><div class="pl2-species-flyout__name">${esc(sp.name || '')}</div>
      <div class="pl2-species-flyout__sci">${esc(sp.scientific_name || '')}</div>
      ${sp.parent_name ? `<div class="pl2-muted">${esc(sp.parent_name)}</div>` : ''}</div>`;
    const r = row.getBoundingClientRect();
    fly.style.left = `${r.right + 8}px`;
    fly.style.top = `${r.top}px`;
    document.body.appendChild(fly);
    this._speciesFlyout = fly;
  }

  hideSpeciesFlyout() { this._speciesFlyout?.remove(); this._speciesFlyout = null; }

  // Scale bar (1 plan unit = 1 cm), ported from v1's grid legend.
  renderScaleBar(state) {
    if (!this.hasScalebarTarget) return;
    const metric = state.settings?.units !== 'imperial';
    const dims = metric
      ? [[1, '1 cm'], [10, '10 cm'], [100, '1 m'], [1000, '10 m'], [10000, '100 m'], [100000, '1 km']]
      : [[2.54, '1 in'], [30.48, '1 ft'], [304.8, '10 ft'], [3048, '100 ft'], [30480, '1000 ft'], [304800, '10k ft']];
    const scale = state.view?.scale || 1;
    const kept = dims.map(([abs, label]) => ({ abs, px: abs * scale, label })).filter((d) => d.px >= 5);
    if (kept.length < 2) { this.scalebarTarget.hidden = true; return; }
    const minor = kept[0]; const major = kept[1];
    const ticks = Math.max(1, Math.round(major.abs / minor.abs));
    const minorPx = major.px / ticks; // exact, no drift
    const W = major.px;
    let lines = '';
    for (let i = 0; i <= ticks; i += 1) {
      const x = (i * minorPx + 0.5).toFixed(2);
      const tall = i === 0 || i === ticks;
      lines += `<line x1="${x}" y1="${tall ? 1 : 4}" x2="${x}" y2="10"/>`;
    }
    lines += `<line x1="0.5" y1="10" x2="${(W + 0.5).toFixed(2)}" y2="10"/>`;
    this.scalebarTarget.hidden = false;
    this.scalebarTarget.innerHTML = `
      <svg class="pl2-scalebar__svg" width="${Math.ceil(W) + 2}" height="12" shape-rendering="crispEdges">${lines}</svg>
      <span>${major.label} / ${minor.label}</span>`;
  }

  // --- side panels (rendered from the store) --------------------------------
  renderPanels(state) {
    const layers = this.element.querySelector('[data-tab-panel="layers"]');
    if (layers) {
      layers.innerHTML = '<h3 class="pl2-section-title">Layers</h3><div class="pl2-layer-list">' + state.layers.map((l) => `
        <div class="pl2-row" data-layer-id="${esc(l.id)}">
          <span class="pl2-drag" data-drag-handle title="Drag to reorder">⠿</span>
          <input type="checkbox" data-pl2="layer-visible" data-id="${esc(l.id)}" ${l.visible === false ? '' : 'checked'} title="Visible">
          <input class="pl2-input pl2-layer-name" data-pl2="layer-rename" data-id="${esc(l.id)}" value="${esc(l.name)}">
          <input type="range" class="pl2-range" min="0" max="1" step="0.05" value="${l.opacity ?? 1}" data-pl2="layer-opacity" data-id="${esc(l.id)}" title="Opacity">
          <button class="pl2-iconbtn" data-pl2="layer-delete" data-id="${esc(l.id)}" title="Delete layer">×</button>
        </div>`).join('') + '</div><button class="pl2-btn" data-pl2="layer-add" style="margin-top:8px">+ Add layer</button>';
      this.initLayerSort(layers.querySelector('.pl2-layer-list'));
    }

    const elements = this.element.querySelector('[data-tab-panel="elements"]');
    if (elements) {
      const sel = new Set(state.selection);
      elements.innerHTML = `<h3 class="pl2-section-title">Elements (${state.elements.length})</h3>` + state.elements.map((el) => {
        const locked = !!el.data?.locked;
        const name = elementLabel(el);
        return `<div class="pl2-row ${sel.has(el.id) ? 'is-selected' : ''} ${locked ? 'is-locked' : ''}" data-pl2="element-select" data-id="${esc(el.id)}">
          <span class="pl2-row__swatch" style="background:${esc(el.data?.fillColor || el.data?.strokeColor || '#ccc')}"></span>
          <span class="pl2-row__name">${esc(name)}</span>
          <button class="pl2-iconbtn pl2-lockbtn" type="button" data-pl2="element-lock" data-id="${esc(el.id)}" title="${locked ? 'Unlock element' : 'Lock element'}" aria-label="${locked ? 'Unlock element' : 'Lock element'}">${locked ? '🔒' : '🔓'}</button>
        </div>`;
      }).join('');
    }

    const plants = this.element.querySelector('[data-tab-panel="plants"]');
    if (plants) {
      const counts = new Map();
      for (const el of state.elements) for (const sid of (el.speciesIds || [])) counts.set(sid, (counts.get(sid) || 0) + 1);
      const rows = [...counts.entries()].map(([sid, n]) => {
        const name = state.species[sid]?.name || `Plant #${sid}`;
        return `<div class="pl2-row" data-pl2="plant-select" data-id="${sid}"><span class="pl2-row__name">${esc(name)}</span><span class="pl2-muted">${n}</span></div>`;
      }).join('');
      plants.innerHTML = `<h3 class="pl2-section-title">Plants in plan (${counts.size})</h3>${rows || '<p class="pl2-muted">No plants yet.</p>'}`;
    }

    const props = this.element.querySelector('[data-tab-panel="properties"]');
    if (props) props.innerHTML = this.propertiesHtml(state);
  }

  // --- lists tab (user's plant lists, lazy-loaded) --------------------------
  async loadLists() {
    if (this._listsLoaded || this._listsLoading || !this.usernameValue) return;
    this._listsLoading = true;
    this.renderListsPanel();
    try {
      const res = await fetch(`/${this.usernameValue}/plans/lists`, { headers: { Accept: 'application/json' } });
      this._lists = res.ok ? await res.json() : {};
    } catch (e) { this._lists = {}; }
    this._listsLoaded = true; this._listsLoading = false;
    this.renderListsPanel();
  }

  renderListsPanel() {
    const panel = this.element.querySelector('[data-tab-panel="lists"]');
    if (!panel) return;
    if (this._listsLoading) { panel.innerHTML = '<h3 class="pl2-section-title">Your lists</h3><p class="pl2-muted">Loading…</p>'; return; }
    const entries = Object.entries(this._lists || {});
    if (!entries.length) { panel.innerHTML = '<h3 class="pl2-section-title">Your lists</h3><p class="pl2-muted">No lists yet.</p>'; return; }
    panel.innerHTML = '<h3 class="pl2-section-title">Your lists</h3>' + entries.map(([id, list]) => `
      <details class="pl2-list">
        <summary>${esc(list.name)} <span class="pl2-muted">(${(list.plants || []).length})</span></summary>
        ${(list.plants || []).map((p, i) => `
          <div class="pl2-result" data-pl2="list-place" data-list="${esc(id)}" data-idx="${i}" title="Click, then click the canvas to place">
            ${p.thumb ? `<img class="pl2-result__img" src="${esc(p.thumb)}" alt="">` : '<span class="pl2-result__img"></span>'}
            <span class="pl2-result__text"><span class="pl2-result__name">${esc(p.name || '')}</span><span class="pl2-result__sci">${esc(p.scientific_name || '')}</span></span>
          </div>`).join('')}
      </details>`).join('');
  }

  propertiesHtml(state) {
    const sel = state.selection;
    if (sel.length === 0) return '<h3 class="pl2-section-title">Properties</h3><p class="pl2-muted">Nothing selected.</p>';
    if (sel.length > 1) return `<h3 class="pl2-section-title">Properties</h3><p class="pl2-muted">${sel.length} elements selected.</p>`;
    const el = state.elements.find((e) => e.id === sel[0]);
    if (!el) return '<h3 class="pl2-section-title">Properties</h3>';
    const d = el.data || {};
    if (el.type === 'image') {
      const layerOpts = state.layers.map((l) => `<option value="${esc(l.id)}" ${d.layer === l.id ? 'selected' : ''}>${esc(l.name)}</option>`).join('');
      return `
        <h3 class="pl2-section-title">Image</h3>
        <div class="pl2-field"><label>Name</label><input class="pl2-input" data-pl2="prop-image-name" value="${esc(d.name || '')}"></div>
        <div class="pl2-field"><label>Width</label><input class="pl2-input" type="number" min="0" data-pl2="prop-width" value="${Math.round(d.width || 0)}"></div>
        <div class="pl2-field"><label>Height</label><input class="pl2-input" type="number" min="0" data-pl2="prop-height" value="${Math.round(d.height || 0)}"></div>
        <div class="pl2-field"><label>Rotation</label><input class="pl2-input" type="number" data-pl2="prop-rotation" value="${Math.round((d.rotation || 0) * 180 / Math.PI)}"></div>
        <div class="pl2-field"><label>Opacity</label><input class="pl2-range" type="range" min="0" max="255" step="1" data-pl2="prop-opacity" value="${d.opacity ?? 200}"></div>
        <label class="pl2-check"><input type="checkbox" data-pl2="prop-visible" ${d.visible === false ? '' : 'checked'}> Visible</label>
        <div class="pl2-field"><label>Layer</label><select class="pl2-input" data-pl2="prop-layer"><option value="">—</option>${layerOpts}</select></div>`;
    }
    const sizeRows = (el.type === 'ellipse')
      ? `<div class="pl2-field"><label>Size</label><input class="pl2-input" type="number" data-pl2="prop-width" value="${Math.round(d.width || 0)}"></div>`
      : (el.type === 'rectangle')
        ? `<div class="pl2-field"><label>Width</label><input class="pl2-input" type="number" data-pl2="prop-width" value="${Math.round(d.width || 0)}"></div>
           <div class="pl2-field"><label>Height</label><input class="pl2-input" type="number" data-pl2="prop-height" value="${Math.round(d.height || 0)}"></div>`
        : '';
    const layerOpts = state.layers.map((l) => `<option value="${esc(l.id)}" ${d.layer === l.id ? 'selected' : ''}>${esc(l.name)}</option>`).join('');
    const rotRow = el.type === 'rectangle'
      ? `<div class="pl2-field"><label>Rotation</label><input class="pl2-input" type="number" data-pl2="prop-rotation" value="${Math.round((d.rotation || 0) * 180 / Math.PI)}"></div>`
      : '';
    const canopyRow = el.type === 'ellipse'
      ? `<div class="pl2-field"><label>Canopy</label><select class="pl2-input" data-pl2="prop-canopy">${['smooth', 'lobed', 'conifer'].map((s) => `<option value="${s}" ${(d.canopyStyle || 'smooth') === s ? 'selected' : ''}>${cap(s)}</option>`).join('')}</select></div>`
      : '';
    const measure = this.measureText(el, state.settings.units);
    const keyNames = ['name', 'scientific_name', 'parent_name'];
    const labels = (d.labels || []).map((lb, i) => {
      if (lb.type === 'custom') {
        return `<div class="pl2-label-row">
          <input type="checkbox" data-pl2="label-visible" data-el-id="${esc(el.id)}" data-i="${i}" ${lb.visible === false ? '' : 'checked'} title="Visible">
          <input class="pl2-input" data-pl2="label-text" data-el-id="${esc(el.id)}" data-i="${i}" value="${esc(lb.value || '')}">
          <button class="pl2-iconbtn" data-pl2="label-remove" data-i="${i}" title="Remove">×</button>
        </div>`;
      }
      const name = state.species[lb.id]?.name || `Plant #${lb.id}`;
      const toggles = keyNames.map((k) => `<label class="pl2-keytoggle"><input type="checkbox" data-pl2="label-key" data-el-id="${esc(el.id)}" data-i="${i}" data-key="${k}" ${(lb.keys || []).includes(k) ? 'checked' : ''}>${k.replace('_', ' ')}</label>`).join('');
      return `<div class="pl2-label-row pl2-label-species">
        <span class="pl2-row__name" data-species-info="${esc(lb.id)}">${esc(name)}</span>
        <button class="pl2-iconbtn" data-pl2="label-remove" data-i="${i}" title="Remove">×</button>
        <div class="pl2-keytoggles">${toggles}</div>
      </div>`;
    }).join('');
    return `
      <h3 class="pl2-section-title">${esc(cap(el.type))}</h3>
      ${sizeRows}
      ${rotRow}
      ${canopyRow}
      ${measure ? `<p class="pl2-muted pl2-measure">${measure}</p>` : ''}
      ${this.sideRows(el, state.settings.units)}
      <div class="pl2-field"><label>Fill</label><input type="color" data-pl2="prop-fill" value="${rgbaToHex(d.fillColor)}"></div>
      <div class="pl2-field"><label>Stroke</label><input type="color" data-pl2="prop-stroke" value="${rgbaToHex(d.strokeColor)}"></div>
      <div class="pl2-field"><label>Thickness</label><input class="pl2-input" type="number" min="0" data-pl2="prop-strokeWidth" value="${d.strokeWidth ?? ''}"><span class="pl2-unit">cm</span></div>
      <div class="pl2-field"><label>Layer</label><select class="pl2-input" data-pl2="prop-layer"><option value="">—</option>${layerOpts}</select></div>
      <div class="pl2-field"><label>Added</label><input class="pl2-input" type="text" placeholder="YYYY-MM" data-pl2="prop-addedDate" value="${esc(d.addedDate || '')}"></div>
      <div class="pl2-field"><label>Removed</label><input class="pl2-input" type="text" placeholder="YYYY-MM" data-pl2="prop-removedDate" value="${esc(d.removedDate || '')}"></div>
      <div class="pl2-field"><label>Notes</label><textarea class="pl2-input" rows="2" data-pl2="prop-notes" placeholder="Notes…">${esc(d.notes || '')}</textarea></div>
      <div class="pl2-section-title" style="margin-top:12px">Plants</div>
      ${(el.speciesIds || []).map((sid) => `<div class="pl2-label-row"><span class="pl2-row__name" data-species-info="${esc(sid)}">${esc(state.species[sid]?.name || `Plant #${sid}`)}</span><button class="pl2-iconbtn" data-pl2="species-remove" data-id="${sid}" title="Remove">×</button></div>`).join('') || '<p class="pl2-muted">No plants.</p>'}
      <button class="pl2-btn" data-pl2="species-add" style="margin-top:6px">+ Add plant</button>
      <div class="pl2-section-title" style="margin-top:12px">Labels</div>
      ${labels || '<p class="pl2-muted">No labels.</p>'}
      <button class="pl2-btn" data-pl2="label-add" style="margin-top:6px">+ Add label</button>`;
  }

  sideRows(el, units) {
    if (el.type !== 'polygon' && el.type !== 'line') return '';
    const sides = straightPathSides(el.data?.coordinates, el.type === 'polygon');
    if (!sides.length) return '<p class="pl2-muted">Curved sides can be resized by dragging their points.</p>';
    const unit = units === 'imperial' ? 'ft' : 'm';
    const rows = sides.map((side, index) => `
      <div class="pl2-field"><label>Side ${side.sideNumber} length</label>
        <input class="pl2-input" type="number" min="0" step="0.01" data-pl2="prop-side" data-i="${index}" value="${displaySideLength(side.length, units).toFixed(2)}"><span class="pl2-unit">${unit}</span></div>`).join('');
    return `<p class="pl2-muted">Type an exact length for each straight side. Curved sides stay draggable.</p>${rows}`;
  }

  // Area (ellipse/rectangle/polygon), perimeter (polygon) or length (line) in the
  // active unit system. Plan units are centimetres (1 unit = 1 cm).
  measureText(el, units) {
    const imperial = units === 'imperial';
    const d = el.data || {};
    const area = (cm2) => (imperial ? `Area ${(cm2 / 929.0304).toFixed(2)} ft²` : `Area ${(cm2 / 10000).toFixed(2)} m²`);
    const length = (cm) => (imperial ? `Length ${(cm / 30.48).toFixed(2)} ft` : `Length ${(cm / 100).toFixed(2)} m`);
    const perimeter = (cm) => (imperial ? `Perimeter ${(cm / 30.48).toFixed(2)} ft` : `Perimeter ${(cm / 100).toFixed(2)} m`);
    if (el.type === 'ellipse') { const r = (d.width || 0) / 2; return area(Math.PI * r * r); }
    if (el.type === 'rectangle') return area((d.width || 0) * (d.height || 0));
    const pts = (d.coordinates || []).filter((c) => Array.isArray(c) && typeof c[0] === 'number');
    if (el.type === 'polygon' && pts.length >= 3) {
      let s = 0;
      let per = 0;
      for (let i = 0; i < pts.length; i += 1) {
        const a = pts[i]; const b = pts[(i + 1) % pts.length];
        s += a[0] * b[1] - b[0] * a[1];
        per += Math.hypot(b[0] - a[0], b[1] - a[1]);
      }
      return `${area(Math.abs(s) / 2)} · ${perimeter(per)}`;
    }
    if (el.type === 'line' && pts.length >= 2) {
      let len = 0;
      for (let i = 1; i < pts.length; i += 1) len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
      return length(len);
    }
    return '';
  }

  onPanelChange(e) {
    const kind = e.target.dataset.pl2;
    if (!kind) return;
    if (kind === 'layer-visible') {
      const id = e.target.dataset.id; const vis = e.target.checked;
      this.commit(this.takeSnapshot(), () => {
        this.store.state.layers = this.store.state.layers.map((l) => (l.id === id ? { ...l, visible: vis } : l));
      });
      return;
    }
    if (kind === 'layer-opacity') {
      const id = e.target.dataset.id; const op = parseFloat(e.target.value);
      this.commit(this.takeSnapshot(), () => {
        this.store.state.layers = this.store.state.layers.map((l) => (l.id === id ? { ...l, opacity: op } : l));
      });
      return;
    }
    if (kind === 'layer-rename') {
      const id = e.target.dataset.id; const name = e.target.value;
      this.commit(this.takeSnapshot(), () => {
        this.store.state.layers = this.store.state.layers.map((l) => (l.id === id ? { ...l, name } : l));
      });
      return;
    }
    const el = this.selectedElement();
    if (!el) return;
    const i = e.target.dataset.i != null ? Number(e.target.dataset.i) : null;
    // The panel is rebuilt on every selection change, so a `change` event can
    // fire on a label input that belonged to a now-deselected element. Drop it
    // when the input's element no longer matches, and skip an out-of-range
    // index, to avoid a write to the wrong element or a crash on `d.labels[i]`.
    if (kind === 'label-visible' || kind === 'label-text' || kind === 'label-key') {
      if (e.target.dataset.elId !== el.id) return;
      if (i == null || i < 0 || i >= (el.data.labels || []).length) return;
    }
    if (kind === 'prop-side') {
      const input = e.target.value.trim();
      const displayLength = Number(input);
      const sides = straightPathSides(el.data?.coordinates, el.type === 'polygon');
      const side = sides[i];
      if (input === '' || !side || !Number.isFinite(displayLength) || displayLength < 0) return;
      const desiredLength = planSideLength(displayLength, this.store.state.settings.units);
      this.commit(this.takeSnapshot(), () => setStraightSideLength(el.data.coordinates, side, desiredLength));
      return;
    }
    this.commit(this.takeSnapshot(), () => {
      const d = el.data;
      if (kind === 'prop-image-name') d.name = e.target.value;
      else if (kind === 'prop-fill') d.fillColor = hexToRgba(e.target.value, d.fillColor);
      else if (kind === 'prop-stroke') d.strokeColor = hexToRgba(e.target.value, d.strokeColor);
      else if (kind === 'prop-strokeWidth') d.strokeWidth = e.target.value === '' ? null : Number(e.target.value);
      else if (kind === 'prop-width') { d.width = Number(e.target.value); if (el.type === 'ellipse') d.height = d.width; }
      else if (kind === 'prop-height') d.height = Number(e.target.value);
      else if (kind === 'prop-rotation') d.rotation = Number(e.target.value) * Math.PI / 180;
      else if (kind === 'prop-opacity') d.opacity = Number(e.target.value);
      else if (kind === 'prop-visible') d.visible = e.target.checked;
      else if (kind === 'prop-layer') d.layer = e.target.value || null;
      else if (kind === 'prop-addedDate') d.addedDate = e.target.value.trim() || null;
      else if (kind === 'prop-removedDate') d.removedDate = e.target.value.trim() || null;
      else if (kind === 'prop-notes') d.notes = e.target.value || null;
      else if (kind === 'prop-canopy') { d.canopyStyle = e.target.value; if (d.canopyStyle !== 'smooth' && !d.canopySeed) d.canopySeed = Math.floor(Math.random() * 100000); }
      else if (kind === 'label-visible') { d.labels[i].visible = e.target.checked; }
      else if (kind === 'label-text') { d.labels[i].value = e.target.value; }
      else if (kind === 'label-key') {
        const keys = new Set(d.labels[i].keys || []);
        e.target.checked ? keys.add(e.target.dataset.key) : keys.delete(e.target.dataset.key);
        d.labels[i].keys = [...keys];
      }
    });
  }

  onPanelClick(e) {
    // Close the zoom menu when clicking outside the zoom control.
    if (this.hasZoomMenuTarget && !this.zoomMenuTarget.hidden && !e.target.closest('.pl2-zoomctl')) {
      this.zoomMenuTarget.hidden = true;
    }
    const target = e.target.closest('[data-pl2]');
    if (!target) return;
    const kind = target.dataset.pl2;
    if (kind === 'element-lock') {
      const el = this.store.state.elements.find((element) => element.id === target.dataset.id);
      if (!el) return;
      this.commit(this.takeSnapshot(), () => {
        el.data.locked = !el.data.locked;
        this.store.state.selection = this.store.state.selection.filter((id) => id !== el.id);
      });
    } else if (kind === 'element-select') {
      const el = this.store.state.elements.find((element) => element.id === target.dataset.id);
      if (el && !el.data?.locked) this.store.set({ selection: [el.id] });
    } else if (kind === 'layer-delete') {
      const id = target.dataset.id;
      this.commit(this.takeSnapshot(), () => {
        this.store.state.layers = this.store.state.layers.filter((l) => l.id !== id);
        for (const el of this.store.state.elements) if (el.data.layer === id) el.data.layer = null;
        this.store.emit();
      });
    } else if (kind === 'layer-add') {
      this.commit(this.takeSnapshot(), () => {
        this.store.state.layers = [...this.store.state.layers, { id: `layer-${Date.now()}`, name: 'New layer', opacity: 1, visible: true }];
        this.store.emit();
      });
    } else if (kind === 'place') {
      const h = this._hits[Number(target.dataset.idx)];
      if (h) this.startPlacement(h);
    } else if (kind === 'plant-select') {
      const sid = Number(target.dataset.id);
      this.store.set({ selection: this.store.state.elements.filter((el) => (el.speciesIds || []).includes(sid) && !el.data?.locked).map((el) => el.id) });
    } else if (kind === 'list-place') {
      const list = this._lists?.[target.dataset.list];
      const plant = list?.plants?.[Number(target.dataset.idx)];
      if (plant) this.startPlacement(plant);
    } else if (kind === 'label-remove') {
      const el = this.selectedElement(); const i = Number(target.dataset.i);
      if (el) this.commit(this.takeSnapshot(), () => { el.data.labels.splice(i, 1); this.store.emit(); });
    } else if (kind === 'label-add') {
      const el = this.selectedElement();
      if (el) this.commit(this.takeSnapshot(), () => {
        el.data.labels = [...(el.data.labels || []), { type: 'custom', value: 'Label', visible: true }];
        this.store.emit();
      });
    } else if (kind === 'species-remove') {
      const sid = Number(target.dataset.id); const el = this.selectedElement();
      if (el) this.commit(this.takeSnapshot(), () => {
        el.speciesIds = (el.speciesIds || []).filter((x) => x !== sid);
        el.data.labels = (el.data.labels || []).filter((l) => !(l.type === 'species' && l.id === sid));
        this.store.emit();
      });
    } else if (kind === 'species-add') {
      this.openSpeciesSearch((plant) => this.addSpeciesToSelected(plant));
    }
  }

  addSpeciesToSelected(plant) {
    const el = this.selectedElement();
    if (!el) return;
    this.store.state.species = { ...this.store.state.species, [plant.id]: plant };
    this.commit(this.takeSnapshot(), () => {
      if (!(el.speciesIds || []).includes(plant.id)) el.speciesIds = [...(el.speciesIds || []), plant.id];
      const keys = plant.type !== 'Variety' ? ['name', 'scientific_name'] : ['parent_name', 'name', 'scientific_name'];
      el.data.labels = [...(el.data.labels || []), { type: 'species', id: plant.id, keys }];
      this.store.emit();
    });
  }

  openSpeciesSearch(onPick) {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>Add a plant</h3></header>
        <section class="modal-content">
          <input class="pl2-input" type="search" placeholder="Search plants…" data-sp="input" style="width:100%">
          <div class="pl2-search-results" data-sp="results" style="margin-top:8px"></div>
        </section>
        <div class="modal-footer"><button class="btn-clear-light" data-act="close" type="button">Cancel</button></div>
      </div>`;
    const close = () => overlay.remove();
    const input = overlay.querySelector('[data-sp="input"]');
    const results = overlay.querySelector('[data-sp="results"]');
    let hits = []; let t;
    input.addEventListener('input', () => {
      clearTimeout(t);
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; return; }
      t = setTimeout(async () => {
        try { const res = await searchPlants({ q, limit: 20 }); hits = res.hits || []; } catch (e) { hits = []; }
        results.innerHTML = hits.map((h, i) => `<div class="pl2-result" data-i="${i}">${h.thumb ? `<img class="pl2-result__img" src="${esc(h.thumb)}" alt="">` : '<span class="pl2-result__img"></span>'}<span class="pl2-result__text"><span class="pl2-result__name">${esc(h.name || '')}</span><span class="pl2-result__sci">${esc(h.scientific_name || '')}</span></span></div>`).join('');
      }, 180);
    });
    results.addEventListener('click', (e) => {
      const row = e.target.closest('[data-i]');
      if (!row) return;
      const plant = hits[Number(row.dataset.i)];
      if (plant) { onPick(plant); close(); }
    });
    overlay.querySelector('[data-act="close"]').addEventListener('click', close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
    input.focus();
  }

  selectedElement() {
    const sel = this.store.state.selection;
    const element = sel.length === 1 ? this.store.state.elements.find((e) => e.id === sel[0]) : null;
    return element?.data?.locked ? null : element;
  }

  // --- plant search + placement --------------------------------------------
  search(event) {
    const q = event.target.value.trim();
    clearTimeout(this._searchT);
    if (!q) { this.searchResultsTarget.innerHTML = ''; this._hits = []; return; }
    this._searchT = setTimeout(() => this.runSearch(q), 180);
  }

  async runSearch(q) {
    try {
      const res = await searchPlants({ q, limit: 30 });
      this._hits = res.hits || [];
      this.renderSearchResults(this._hits);
    } catch (e) {
      this.searchResultsTarget.innerHTML = '<p class="pl2-muted">Search unavailable.</p>';
    }
  }

  renderSearchResults(hits) {
    this.searchResultsTarget.innerHTML = hits.map((h, i) => `
      <div class="pl2-result" data-pl2="place" data-idx="${i}" title="Click, then click the canvas to place">
        ${h.thumb ? `<img class="pl2-result__img" src="${esc(h.thumb)}" alt="">` : '<span class="pl2-result__img"></span>'}
        <span class="pl2-result__text">
          <span class="pl2-result__name">${esc(h.name || '')}</span>
          <span class="pl2-result__sci">${esc(h.scientific_name || '')}</span>
        </span>
      </div>`).join('');
  }

  startPlacement(species) {
    this.store.state.species = { ...this.store.state.species, [species.id]: species };
    this.interactions.cancelPoly();
    this.renderer.clearPreview();
    this.store.set({ activeTool: 'placement', pendingSpecies: species });
    this.renderer.setDraggable(false);
    this.syncToolButtons();
    this.closePanel(); // mobile: get out of the way so the canvas is tappable
  }

  placeSpecies(x, y, sticky = false) {
    const species = this.store.state.pendingSpecies;
    if (!species) return;
    const el = makePlantElement(species, x, y);
    this.commit(this.takeSnapshot(), () => { this.store.state.elements = [...this.store.state.elements, el]; });
    // Default: drop the plant, then return to select so it can be moved/edited.
    // Hold Shift to stay in placement and drop several (matches v1).
    if (!sticky) { this.applyTool('select'); this.store.set({ selection: [el.id] }); }
  }

  // --- keyboard -------------------------------------------------------------
  onKey(e) {
    const tag = (e.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
    const mod = e.metaKey || e.ctrlKey;

    if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); this.save(); return; }
    if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? this.redo() : this.undo(); return; }
    if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); this.redo(); return; }
    if (mod && e.key.toLowerCase() === 'c') { e.preventDefault(); this.copySelection(); return; }
    if (mod && e.key.toLowerCase() === 'v') { e.preventDefault(); this.pasteClipboard(); return; }
    if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); this.cloneSelection(); return; }
    if (e.key === ']') { e.preventDefault(); e.shiftKey ? this.bringToFront() : this.bringForward(); return; }
    if (e.key === '[') { e.preventDefault(); e.shiftKey ? this.sendToBack() : this.sendBackward(); return; }

    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); this.deleteSelection(); return; }
    if (e.key === 'Escape') {
      this.interactions.cancelPoly(); this.renderer.clearPreview();
      if (this.store.state.activeTool === 'calibrate') { this.interactions._calib = null; this.applyTool('select'); }
      else if (this.store.state.activeTool === 'placement') this.applyTool('select');
      else this.store.set({ selection: [] });
      return;
    }
    if (e.key === 'Enter') { this.interactions.finishPoly?.(); return; }
    if (e.key === '?') { e.preventDefault(); this.openHelp(); return; }

    const nudge = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
    if (nudge) { e.preventDefault(); this.nudge(nudge[0] * (e.shiftKey ? 10 : 1), nudge[1] * (e.shiftKey ? 10 : 1)); return; }

    const toolKey = { v: 'select', h: 'navigate', r: 'rectangle', o: 'ellipse', p: 'polygon', l: 'line', e: 'erase' }[e.key.toLowerCase()];
    if (toolKey && !mod) this.applyTool(toolKey, 'user');
  }

  deleteSelection() {
    const sel = new Set(this.store.state.selection);
    const removable = this.store.state.elements.filter((el) => sel.has(el.id) && !el.data?.locked);
    if (!removable.length) return;
    const ids = new Set(removable.map((el) => el.id));
    this.commit(this.takeSnapshot(), () => {
      this.store.state.elements = this.store.state.elements.filter((el) => !ids.has(el.id));
      this.store.set({ selection: [] });
    });
  }

  nudge(dx, dy) {
    const sel = new Set(this.store.state.selection);
    const movable = this.store.state.elements.some((el) => sel.has(el.id) && !el.data?.locked);
    if (!movable) return;
    this.commit(this.takeSnapshot(), () => {
      for (const el of this.store.state.elements) {
        if (!sel.has(el.id) || el.data?.locked) continue;
        if (el.type === 'ellipse' || el.type === 'rectangle' || el.type === 'image') { el.data.x += dx; el.data.y += dy; }
        else if (Array.isArray(el.data.coordinates)) {
          el.data.coordinates = el.data.coordinates.map((c) => (Array.isArray(c) ? shiftOne(c, dx, dy) : c));
        }
      }
    });
  }

  // --- clipboard / duplicate -----------------------------------------------
  selectedElements() {
    const sel = new Set(this.store.state.selection);
    return this.store.state.elements.filter((el) => sel.has(el.id) && !el.data?.locked);
  }

  copySelection() {
    this.clipboard = this.selectedElements().map(({ id, ...el }) => JSON.parse(JSON.stringify(el)));
  }

  pasteClipboard() { if (this.clipboard?.length) this.duplicateElements(this.clipboard); }

  cloneSelection() {
    const els = this.selectedElements().map(({ id, ...el }) => JSON.parse(JSON.stringify(el)));
    if (els.length) this.duplicateElements(els);
  }

  duplicateElements(elems, offset = 20) {
    const created = elems.map((el, i) => {
      const copy = JSON.parse(JSON.stringify(el));
      copy.id = `el-${Date.now()}-${i}-${Math.round(Math.random() * 1e4)}`;
      offsetElement(copy, offset, offset);
      return copy;
    });
    this.commit(this.takeSnapshot(), () => {
      this.store.state.elements = [...this.store.state.elements, ...created];
      this.store.set({ selection: created.map((c) => c.id) });
    });
  }

  // --- z-order --------------------------------------------------------------
  bringToFront() { this.reorder('front'); }
  sendToBack() { this.reorder('back'); }
  bringForward() { this.reorder('forward'); }
  sendBackward() { this.reorder('backward'); }

  reorder(dir) {
    const sel = new Set(this.store.state.selection.filter((id) => !this.store.state.elements.find((el) => el.id === id)?.data?.locked));
    if (!sel.size) return;
    this.commit(this.takeSnapshot(), () => {
      let els = this.store.state.elements.slice();
      if (dir === 'front') { els = [...els.filter((e) => !sel.has(e.id)), ...els.filter((e) => sel.has(e.id))]; }
      else if (dir === 'back') { els = [...els.filter((e) => sel.has(e.id)), ...els.filter((e) => !sel.has(e.id))]; }
      else {
        const step = dir === 'forward' ? 1 : -1;
        const idxs = els.map((e, i) => [e, i]).filter(([e]) => sel.has(e.id)).map(([, i]) => i);
        for (const i of (step > 0 ? idxs.slice().reverse() : idxs)) {
          const j = i + step;
          if (j < 0 || j >= els.length || sel.has(els[j].id)) continue;
          [els[i], els[j]] = [els[j], els[i]];
        }
      }
      this.store.state.elements = els;
    });
  }

  // --- copy / paste style ---------------------------------------------------
  copyStyle() {
    const el = this.selectedElement();
    if (!el) return;
    const d = el.data || {};
    this.styleClipboard = { fillColor: d.fillColor, strokeColor: d.strokeColor, strokeWidth: d.strokeWidth, canopyStyle: d.canopyStyle };
  }

  pasteStyle() {
    if (!this.styleClipboard) return;
    const sel = new Set(this.store.state.selection);
    if (!sel.size) return;
    this.commit(this.takeSnapshot(), () => {
      for (const el of this.store.state.elements) {
        if (!sel.has(el.id) || el.data?.locked) continue;
        el.data.fillColor = this.styleClipboard.fillColor;
        el.data.strokeColor = this.styleClipboard.strokeColor;
        el.data.strokeWidth = this.styleClipboard.strokeWidth;
        if (el.type === 'ellipse' && this.styleClipboard.canopyStyle != null) el.data.canopyStyle = this.styleClipboard.canopyStyle;
      }
    });
  }

  // --- right-click context menu --------------------------------------------
  openContextMenu(clientX, clientY, el) {
    this.closeContextMenu();
    if (el?.data?.locked) return;
    if (el && !this.store.state.selection.includes(el.id)) this.store.set({ selection: [el.id] });
    const hasSel = this.store.state.selection.length > 0;
    const item = (label, action, disabled) => `<button data-ctx="${action}"${disabled ? ' disabled' : ''}>${label}</button>`;
    const menu = document.createElement('div');
    menu.className = 'pl2-ctxmenu';
    const isPath = !!this.singleSelectedPath();
    menu.innerHTML = [
      item('Duplicate', 'clone', !hasSel),
      item('Copy', 'copy', !hasSel),
      item('Paste', 'paste', !this.clipboard?.length),
      item('Delete', 'delete', !hasSel),
      '<hr>',
      item('Bring to front', 'front', !hasSel),
      item('Send to back', 'back', !hasSel),
      '<hr>',
      item('Copy style', 'copy-style', this.store.state.selection.length !== 1),
      item('Paste style', 'paste-style', !this.styleClipboard || !hasSel),
      isPath ? '<hr>' + item('Smooth (curves)', 'smooth', false) + item('Straighten', 'straighten', false) : '',
    ].join('');
    menu.style.left = `${clientX}px`;
    menu.style.top = `${clientY}px`;
    menu.addEventListener('click', (e) => {
      const b = e.target.closest('[data-ctx]');
      if (!b || b.disabled) return;
      this.runCtx(b.dataset.ctx);
      this.closeContextMenu();
    });
    this.element.appendChild(menu); // inside .pl2 so the CSS vars resolve
    this._ctxMenu = menu;
    this._ctxClose = (e) => { if (!menu.contains(e.target)) this.closeContextMenu(); };
    setTimeout(() => document.addEventListener('mousedown', this._ctxClose), 0);
  }

  closeContextMenu() {
    this._ctxMenu?.remove();
    this._ctxMenu = null;
    if (this._ctxClose) { document.removeEventListener('mousedown', this._ctxClose); this._ctxClose = null; }
  }

  runCtx(action) {
    const map = {
      clone: () => this.cloneSelection(), copy: () => this.copySelection(), paste: () => this.pasteClipboard(),
      delete: () => this.deleteSelection(), front: () => this.bringToFront(), back: () => this.sendToBack(),
      'copy-style': () => this.copyStyle(), 'paste-style': () => this.pasteStyle(),
      smooth: () => this.smoothSelection(), straighten: () => this.straightenSelection(),
    };
    (map[action] || (() => {}))();
  }

  // The selected element if it's a single polygon/line, else null.
  singleSelectedPath() {
    const sel = this.store.state.selection;
    if (sel.length !== 1) return null;
    const el = this.store.state.elements.find((e) => e.id === sel[0]);
    return el && (el.type === 'polygon' || el.type === 'line') ? el : null;
  }

  // Turn the path into a genuinely smooth curve through its points: a
  // Catmull-Rom spline converted to cubic beziers, so it visibly bends and every
  // segment exposes two draggable control points. Polygons close smoothly.
  smoothSelection() {
    const el = this.singleSelectedPath();
    if (!el) return;
    const pts = (el.data.coordinates || []).filter((c) => Array.isArray(c) && typeof c[0] === 'number').map((c) => [c[0], c[1]]);
    if (pts.length < 2) return;
    const closed = el.type === 'polygon';
    const n = pts.length;
    const at = (i) => (closed ? pts[((i % n) + n) % n] : pts[Math.max(0, Math.min(n - 1, i))]);
    const out = [[pts[0][0], pts[0][1]]];
    const segs = closed ? n : n - 1;
    for (let i = 0; i < segs; i += 1) {
      const p0 = at(i - 1); const p1 = at(i); const p2 = at(i + 1); const p3 = at(i + 2);
      const c1x = p1[0] + (p2[0] - p0[0]) / 6; const c1y = p1[1] + (p2[1] - p0[1]) / 6;
      const c2x = p2[0] - (p3[0] - p1[0]) / 6; const c2y = p2[1] - (p3[1] - p1[1]) / 6;
      if (closed && i === segs - 1) out.push(['closing', 'bezier', c1x, c1y, c2x, c2y]);
      else out.push([p2[0], p2[1], 'bezier', c1x, c1y, c2x, c2y]);
    }
    this.commit(this.takeSnapshot(), () => { el.data.coordinates = out; });
  }

  // Drop all curve/bezier control data (and any explicit close) — straight only.
  straightenSelection() {
    const el = this.singleSelectedPath();
    if (!el) return;
    this.commit(this.takeSnapshot(), () => {
      el.data.coordinates = el.data.coordinates
        .filter((c) => Array.isArray(c) && typeof c[0] === 'number')
        .map((c) => [c[0], c[1]]);
    });
  }

  // --- error modal ----------------------------------------------------------
  showError(message, sessionExpired = false, title = "Couldn't save your plan") {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div class="modal-container">
        <header class="modal-header"><h3>${escapeHtml(title)}</h3></header>
        <section class="modal-content"><p>${escapeHtml(message)}</p></section>
        <div class="modal-footer"><button class="btn-primary" type="button">${sessionExpired ? 'Refresh' : 'OK'}</button></div>
      </div>`;
    const close = () => overlay.remove();
    overlay.querySelector('button').addEventListener('click', sessionExpired ? () => this.refreshWithDraft() : close);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
    this.element.appendChild(overlay);
  }
}

function offsetElement(el, dx, dy) {
  const d = el.data || {};
  if (el.type === 'ellipse' || el.type === 'rectangle' || el.type === 'image') { d.x = (d.x || 0) + dx; d.y = (d.y || 0) + dy; }
  else if (Array.isArray(d.coordinates)) { d.coordinates = d.coordinates.map((c) => (Array.isArray(c) ? shiftOne(c, dx, dy) : c)); }
  if (d.labelX != null) { /* keep label offset */ }
}

function shiftOne(coord, dx, dy) {
  const out = coord.slice();
  for (let i = 0; i < out.length - 1; i += 1) {
    if (typeof out[i] === 'number' && typeof out[i + 1] === 'number') { out[i] += dx; out[i + 1] += dy; i += 1; }
  }
  return out;
}

// Scale every numeric (x,y) pair in a coordinate entry about `origin`.
function scaleCoord(coord, origin, f) {
  const out = coord.slice();
  for (let i = 0; i < out.length - 1; i += 1) {
    if (typeof out[i] === 'number' && typeof out[i + 1] === 'number') {
      out[i] = origin.x + (out[i] - origin.x) * f;
      out[i + 1] = origin.y + (out[i + 1] - origin.y) * f;
      i += 1;
    }
  }
  return out;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
const esc = escapeHtml;
function cap(s) { return s ? s[0].toUpperCase() + s.slice(1) : s; }

function elementLabel(el) {
  if (el.type === 'image') return el.data?.name || 'Image';
  const labels = el.data?.labels || [];
  const custom = labels.find((l) => l.type === 'custom' && l.value);
  if (custom) return custom.value;
  const species = labels.find((l) => l.type === 'species');
  if (species) return `Plant #${species.id}`;
  return cap(el.type);
}

function rgbaToHex(rgba) {
  if (!rgba) return '#000000';
  const m = String(rgba).match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return '#000000';
  return '#' + [m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('');
}

function hexToRgba(hex, prev) {
  const m = String(hex).match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return prev || null;
  const r = parseInt(m[1], 16); const g = parseInt(m[2], 16); const b = parseInt(m[3], 16);
  let alpha = 1;
  const pm = String(prev || '').match(/rgba?\([^)]*,\s*([\d.]+)\s*\)/);
  if (pm) alpha = parseFloat(pm[1]);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
